import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { Button, Tooltip, message } from "antd";
import { FaUserEdit, FaPaperPlane, FaTrashAlt, FaPaperclip, FaFileAlt, FaThumbtack, FaCloudDownloadAlt, FaFilePdf, FaThumbsUp, FaPlus, FaCamera, FaMicrophone, FaVideo, FaDownload, FaTimes, FaPencilAlt, FaReply } from "react-icons/fa";
import { MentionsInput, Mention } from 'react-mentions';
import moment from "moment";
import AuthenticatedImage, { toSecureUrl } from "../../Reuseable/AuthenticatedImage";
import { PreviewZoom } from "../../Reuseable/ImagePreview";

import CrmLeadsServices from "../../../Services/CrmLeadsServices";
import UserProfile from "../../Reuseable/UserProfile..jsx";
import SocketService from "../../../Services/SocketService";
import DataParser from "../../../utlits/Dataparser";
import EmojiPicker, { Theme } from "emoji-picker-react";
import { Popover } from "antd";

const LeadComments = ({ leadId, localNotes, setLocalNotes, mentionUsers, currentUserId, companyId, isModal = false }) => {
    const [newComment, setNewComment] = useState("");
    const [noteAttachments, setNoteAttachments] = useState([]);
    const [isUploding, setIsUploding] = useState(false);
    const [editNoteId, setEditNoteId] = useState(null);
    const [UpdatedNote, setUpdatedNote] = useState("");
    const [activeDropdownId, setActiveDropdownId] = useState(null);
    const [activeReactionNoteId, setActiveReactionNoteId] = useState(null);
    const notePath = process.env.REACT_APP_NOTES_PATH || "https://crm.plan-it.xyz/public/uploads/notes/";
    const userImagePath = process.env.REACT_APP_USER_DOCS_PATH || "https://crm.plan-it.xyz/public/users/docs/";

    const openSecureFile = async (url, filename = "") => {
        try {
            const secureUrl = toSecureUrl(url);
            const token = DataParser.AuthToken();
            const r = await fetch(secureUrl, { headers: { authToken: token } });
            if (!r.ok) throw new Error("not ok");
            const blob = await r.blob();
            const blobUrl = URL.createObjectURL(blob);
            const ext = (filename || url).split(".").pop().toLowerCase();
            const viewable = ["jpg","jpeg","png","gif","webp","pdf","mp4","webm","ogg","mp3","wav"].includes(ext);
            if (viewable) { window.open(blobUrl, "_blank"); }
            else { const a = document.createElement("a"); a.href = blobUrl; a.download = filename || url.split("/").pop(); a.click(); }
            setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
        } catch { window.open(url, "_blank"); }
    };

    const scrollContainerRef = useRef(null);
    const prevLengthRef = useRef(0);
    const isFirstRun = useRef(true);
    const fileInputRef = useRef(null);
    const inputRef = useRef(null);
    const dragCounterRef = useRef(0);
    const [dragOver, setDragOver] = useState(false);

    const handleDragEnter = (e) => {
        e.preventDefault();
        dragCounterRef.current += 1;
        if (e.dataTransfer.items?.length > 0) setDragOver(true);
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        dragCounterRef.current -= 1;
        if (dragCounterRef.current <= 0) { dragCounterRef.current = 0; setDragOver(false); }
    };
    const handleDragOver = (e) => { e.preventDefault(); };
    const allowedNoteExts = /\.(png|jpg|jpeg|pdf|docx|csv|txt|zip|rar|sql|ppt|xls|xlsx|svg|webp|avif|doc)$/i;
    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        dragCounterRef.current = 0;
        const files = Array.from(e.dataTransfer.files).filter(f => allowedNoteExts.test(f.name));
        if (files.length > 0) setNoteAttachments(prev => [...prev, ...files]);
    };
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [replyingTo, setReplyingTo] = useState(null);
    const [noteToDelete, setNoteToDelete] = useState(null);

    const scrollToBottom = (isInitial = false) => {
        const performScroll = () => {
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTo({
                    top: scrollContainerRef.current.scrollHeight,
                    behavior: isInitial ? "auto" : "smooth"
                });
            }
        };

        if (isInitial) {
            setTimeout(performScroll, 100);
        } else {
            performScroll();
        }
    };

    useEffect(() => {
        if (isFirstRun.current || localNotes.length > prevLengthRef.current) {
            scrollToBottom(isFirstRun.current);
            isFirstRun.current = false;
        }
        prevLengthRef.current = localNotes.length;
    }, [localNotes]);

    const handleReaction = async (noteId, emoji) => {
        // 🚀 Optimistic Update for instant feedback
        const originalNotes = [...localNotes];
        setLocalNotes(prev => prev.map(n => {
            if (String(n.id) === String(noteId)) {
                const reactions = [...(n.reactions || [])];
                const existingIdx = reactions.findIndex(r => String(r.user_id) === String(currentUserId) && r.emoji === emoji);
                
                if (existingIdx > -1) {
                    reactions.splice(existingIdx, 1);
                } else {
                    reactions.push({ user_id: currentUserId, user_name: DataParser.CompanyUserName(), emoji });
                }
                return { ...n, reactions };
            }
            return n;
        }));

        try {
            const res = await CrmLeadsServices.updateNoteReaction(noteId, {
                emoji,
                company_id: companyId,
                user_id: currentUserId,
                user_name: DataParser.CompanyUserName()
            });
            
            // Sync with server response
            if (res && res.Good !== false) {
                setLocalNotes(prev => prev.map(n => {
                    if (String(n.id) === String(noteId)) {
                        return { ...n, reactions: res.data || [] };
                    }
                    return n;
                }));
            } else {
                setLocalNotes(originalNotes);
                message.error("Failed to update reaction");
            }
        } catch (err) { 
            console.error("Failed to update reaction:", err); 
            setLocalNotes(originalNotes);
        }
    };

    const handleEditNote = (note) => {
        setEditNoteId(note.id);
        setUpdatedNote(note.notes || note.content || "");
        setActiveDropdownId(null);
    };

    const handleSaveEdit = async (noteId) => {
        if (!UpdatedNote.trim()) return;
        try {
            const res = await CrmLeadsServices.updateNote(noteId, { 
                notes: UpdatedNote,
                company_id: companyId,
                is_text_edit: true
            });
            if (res && res.Good !== false) {
                setEditNoteId(null);
                setUpdatedNote("");
                setLocalNotes(prev => prev.map(n => n.id === noteId ? { ...n, notes: UpdatedNote, is_edited: 1 } : n));
            }
        } catch (err) { console.error("Failed to update note:", err); }
    };

    const handlePinNote = async (note) => {
        try {
            const newPinState = Number(note.pin_top) === 1 ? 0 : 1;
            const res = await CrmLeadsServices.updateNote(note.id, { 
                pin_top: newPinState,
                notes: note.notes || note.content || "",
                company_id: companyId 
            });
            if (res.Good) {
                setLocalNotes(prev => prev.map(n => {
                    if (String(n.id) === String(note.id)) {
                        return { ...n, pin_top: newPinState };
                    }
                    // If we are pinning a new note, explicitly unpin ALL others
                    if (newPinState === 1) {
                        return { ...n, pin_top: 0 };
                    }
                    return n;
                }));
                setActiveDropdownId(null);
            }
        } catch (err) { message.error("Failed to pin comment"); }
    };

    const handleDeleteNote = async (noteId) => {
        try {
            const res = await CrmLeadsServices.deleteNote(noteId, companyId);
            if (res && res.Good !== false) {
                setLocalNotes(prev => prev.filter(n => n.id !== noteId));
                message.success("Comment deleted");
            }
        } catch (err) { 
            console.error("Failed to delete note:", err); 
            message.error("Failed to delete comment");
        }
    };

    const handleReply = (note) => {
        setReplyingTo({
            id: note.id,
            user_name: note.user_name,
            content: (note.notes || note.content || "Attachment").substring(0, 100).replace(/@\[([^\]]+)\]\([^)]+\)/g, '@$1')
        });
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const handleAddNote = async () => {
        if (!newComment.trim() && noteAttachments.length === 0) return;
        
        // 🚀 Optimistic Update
        const optimisticNote = {
            id: Date.now(),
            notes: newComment,
            user_id: currentUserId,
            user_name: DataParser.CompanyUserName(),
            user_image: DataParser.CompanyUserImage(),
            createdAt: new Date(),
            reply_to: replyingTo,
            attachment_file: noteAttachments.length > 0 ? `__preview__${noteAttachments[0].name}` : undefined,
            isOptimistic: true
        };

        const originalNotes = [...localNotes];
        setLocalNotes(prev => [...prev, optimisticNote]);
        
        // Clear inputs immediately
        setNewComment(""); 
        setNoteAttachments([]);
        setReplyingTo(null);

        setIsUploding(true);
        const formData = new FormData();
        formData.append("notes", optimisticNote.notes);
        formData.append("company_id", companyId);
        formData.append("lead_id", leadId);
        formData.append("company_identifier", DataParser.CompanyIdentifier());
        if (optimisticNote.reply_to) {
            formData.append("reply_to", JSON.stringify(optimisticNote.reply_to));
        }
        
        if (noteAttachments.length > 0) {
            noteAttachments.forEach((file) => {
                formData.append("file", file);
            });
        }
        
        try {
            const res = await CrmLeadsServices.createNote(formData);
            if (!res || res.Good === false) {
                setLocalNotes(originalNotes);
                message.error("Failed to add comment");
            } else {
                // Backend returns "Comment added successfully" (string), not the note object.
                // Only replace the optimistic note if we got an actual object back.
                // If not, leave the optimistic note in place — the socket event will arrive
                // shortly with the real note data and will replace it then.
                const noteData = res.data?.note;
                const realNote = (noteData && typeof noteData === 'object' && noteData.id)
                    ? noteData
                    : (res.data && typeof res.data === 'object' && res.data.id)
                    ? res.data
                    : null;
                if (realNote) {
                    setLocalNotes(prev => prev.map(n =>
                        n.isOptimistic && n.id === optimisticNote.id
                            ? { ...realNote, isOptimistic: false }
                            : n
                    ));
                }
                // else: leave optimistic note; socket will replace it with real data
            }
        } catch (err) {
            setLocalNotes(originalNotes);
            message.error("Failed to add comment");
        } finally { setIsUploding(false); }
    };

    const htmlToFormattedText = (html) => {
        return html
            .replace(/<li[^>]*>/gi, '\n• ')
            .replace(/<\/li>/gi, '')
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<\/p>/gi, '\n')
            .replace(/<\/div>/gi, '\n')
            .replace(/<\/h[1-6]>/gi, '\n')
            .replace(/<[^>]+>/g, '')
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&nbsp;/g, ' ')
            .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
            .replace(/• ([ \t]*\n)+[ \t]*/g, '• ')
            .replace(/\n{3,}/g, '\n\n')
            .trim();
    };

    const handleNotePaste = (e, currentValue, setValue) => {
        const items = e.clipboardData?.items;
        if (items) {
            for (const item of Array.from(items)) {
                if (item.type.startsWith('image/')) {
                    e.preventDefault();
                    e.stopPropagation();
                    const file = item.getAsFile();
                    if (file) {
                        const ext = item.type.split('/')[1]?.replace('jpeg', 'jpg') || 'png';
                        const namedFile = new File([file], `pasted-image-${Date.now()}.${ext}`, { type: item.type });
                        setNoteAttachments(prev => [...prev, namedFile]);
                    }
                    return;
                }
            }
        }
        const html = e.clipboardData?.getData('text/html');
        if (!html) return;
        e.preventDefault();
        e.stopPropagation();
        const formatted = htmlToFormattedText(html);
        if (!formatted) return;
        // If HTML conversion lost line breaks compared to plain text, use plain text instead
        const plain = e.clipboardData?.getData('text/plain') || '';
        const htmlNewlines = (formatted.match(/\n/g) || []).length;
        const plainNewlines = (plain.match(/\n/g) || []).length;
        const textToInsert = plainNewlines > htmlNewlines
            ? plain.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
            : formatted;
        const start = e.target.selectionStart || 0;
        const end = e.target.selectionEnd || 0;
        setValue(currentValue.substring(0, start) + textToInsert + currentValue.substring(end));
    };

    const renderCommentWithMentions = (text) => {
        if (!text) return "";
        const renderInline = (lineText, keyPrefix) =>
            lineText.split(/(@\[[^\]]+\]\(\d+\)|https?:\/\/[^\s]+)/g).map((part, i) => {
                if (!part) return null;
                if (/^https?:\/\/[^\s]+$/.test(part))
                    return <a key={`${keyPrefix}-u${i}`} href={part} target="_blank" rel="noopener noreferrer" style={{ color: "#0DDFAB", textDecoration: "underline" }} onClick={e => e.stopPropagation()}>{part}</a>;
                const m = part.match(/^@\[([^\]]+)\]\((\d+)\)$/);
                if (m) return <span key={`${keyPrefix}-m${i}`} className="mentions-pill">@{m[1]}</span>;
                return part;
            });

        const result = [];
        text.split('\n').forEach((line, li) => {
            if (li > 0) result.push(<br key={`br${li}`} />);
            const bullet = line.match(/^[ \t]*([-*•+])[ \t]+(.*)/);
            if (bullet) {
                result.push(<span key={`ln${li}`}>{'• '}{renderInline(bullet[2], `ln${li}`)}</span>);
            } else {
                renderInline(line, `ln${li}`).filter(Boolean).forEach(el => result.push(el));
            }
        });
        return result;
    };

    const chronologicalNotes = [...localNotes].sort((a, b) => {
        return new Date(a.created_at || a.createdAt || '') - new Date(b.created_at || b.createdAt || '');
    });
    
    const pinnedNote = localNotes.find(n => Number(n.pin_top) === 1);

    return (
        <>
        <div className="d-flex flex-column" style={{ overflow: 'visible', height: isModal ? '100%' : 'auto', position: 'relative' }} onDragEnter={handleDragEnter} onDragLeave={handleDragLeave} onDragOver={handleDragOver} onDrop={handleDrop}>
            {dragOver && (
                <div style={{ position: 'absolute', inset: 0, zIndex: 200, background: 'rgba(13,223,171,0.10)', border: '2px dashed #0DDFAB', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                    <div style={{ background: '#fff', borderRadius: '12px', padding: '20px 32px', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }}>
                        <i className="uil-import" style={{ fontSize: '32px', color: '#0DDFAB', display: 'block', marginBottom: '8px' }} />
                        <div style={{ fontWeight: 700, fontSize: '15px', color: '#1D1D1D' }}>Drop files to attach</div>
                    </div>
                </div>
            )}
            <style>{`
                .mentions-input-field__input {
                    outline: none !important;
                    line-height: 1.6 !important;
                    font-family: inherit !important;
                    font-size: 14px !important;
                }
                .mentions-input-field__highlighter {
                    white-space: pre-wrap !important;
                    word-break: break-word !important;
                    line-height: 1.6 !important;
                    font-family: inherit !important;
                    font-size: 14px !important;
                    overflow: visible !important;
                }
                .mentions-pill {
                    background-color: #0DDFAB !important;
                    color: #1d1d1d !important;
                    padding: 1px 6px !important;
                    margin: 0 4px 0 0 !important;
                    border-radius: 4px;
                    font-weight: 600;
                    font-size: 12px !important;
                    display: inline-block !important;
                    white-space: nowrap !important;
                    vertical-align: middle !important;
                    line-height: 1.5 !important;
                    box-sizing: border-box !important;
                }
                /* Highlighter layer — colour matches background so text is invisible
                   but the pill rectangle fully covers the name (same pattern as chat) */
                .mentions-input-field__highlighter .mentions-pill {
                    color: #0DDFAB !important;
                    background-color: #0DDFAB !important;
                    padding: 0 !important;
                    margin: 0 !important;
                    display: inline !important;
                    font-size: 14px !important;
                    font-weight: normal !important;
                    border-radius: 4px !important;
                    line-height: inherit !important;
                    white-space: pre-wrap !important;
                    word-break: break-word !important;
                    box-decoration-break: clone !important;
                    -webkit-box-decoration-break: clone !important;
                    box-sizing: border-box !important;
                }
                .custom-comment-scroll::-webkit-scrollbar { width: 4px; }
                .custom-comment-scroll::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
                .custom-comment-scroll::-webkit-scrollbar-thumb { background: #0DDFAB; border-radius: 10px; }
                
                textarea:focus,
                [class*="__input"]:focus,
                [class*="__control"]:focus-within {
                    outline: none !important;
                    border: none !important;
                    box-shadow: none !important;
                }
                .note-card {
                    background: #fff;
                    border: 1px solid #eaecf0;
                    border-radius: 8px;
                    padding: 6px 10px 4px 10px;
                    margin-bottom: 8px;
                    transition: all 0.2s;
                    position: relative;
                }
                .note-card:hover {
                    border-color: #1d1d1d;
                }
                .note-card.is-pinned {
                    background: #f0fdfa;
                    position: sticky;
                    top: 0;
                    z-index: 10;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
                    margin-bottom: 8px;
                }
                .note-header {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    margin-bottom: 4px;
                }
                .note-user-info {
                    flex: 1;
                    display: flex;
                    align-items: center;
                }
                .note-user-name {
                    font-weight: 600;
                    font-size: 11px;
                    color: #1d1d1d;
                    white-space: nowrap;
                }
                .note-date {
                    font-size: 10px;
                    color: #9ca3af;
                    margin-left: 8px;
                    white-space: nowrap;
                }
                .note-actions {
                    display: flex;
                    gap: 12px;
                    color: #9ca3af;
                    align-items: center;
                    opacity: 0;
                    transition: opacity 0.15s ease;
                }
                .note-card:hover .note-actions {
                    opacity: 1;
                }
                .note-card.is-pinned .note-actions {
                    opacity: 1;
                }
                .note-action-icon {
                    cursor: pointer;
                    font-size: 10px;
                    transition: color 0.2s;
                }
                .note-action-icon:hover {
                    color: #1d1d1d;
                }
                .note-content {
                    font-size: 14px;
                    color: #374151;
                    line-height: 1.4;
                    margin-bottom: 4px;
                    word-break: break-word;
                }
                .note-footer {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    color: #9ca3af;
                    overflow: hidden;
                    max-height: 0;
                    opacity: 0;
                    transition: max-height 0.18s ease, opacity 0.15s ease;
                }
                .note-card:hover .note-footer {
                    max-height: 28px;
                    opacity: 1;
                }
                .note-reactions {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 4px;
                    padding: 3px 0 1px;
                }
                .footer-icon {
                    cursor: pointer;
                    font-size: 11px;
                    transition: color 0.2s;
                }
                .footer-icon:hover {
                    color: #1d1d1d;
                }
                .emoji-mask-icon {
                    display: inline-block;
                    width: 16px;
                    height: 16px;
                    background-color: currentColor;
                    -webkit-mask-size: contain;
                    mask-size: contain;
                    -webkit-mask-repeat: no-repeat;
                    mask-repeat: no-repeat;
                    -webkit-mask-position: center;
                    mask-position: center;
                    flex-shrink: 0;
                }
                .reply-quote {
                    background: #f9fafb;
                    border-left: 3px solid #0DDFAB;
                    padding: 8px 12px;
                    border-radius: 4px;
                    font-size: 12px;
                    margin-bottom: 8px;
                }
                .quote-user {
                    font-weight: 700;
                    color: #0DDFAB;
                    margin-bottom: 2px;
                }
                .quote-content {
                    color: #6b7280;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
                .reply-preview-banner {
                    background: #f0fdfa;
                    border-radius: 8px;
                    padding: 8px 12px;
                    margin-bottom: 8px;
                    border-left: 4px solid #0DDFAB;
                }
                .reply-label {
                    font-size: 12px;
                    font-weight: 600;
                    color: #0DDFAB;
                }
                .close-reply {
                    cursor: pointer;
                    font-size: 12px;
                    color: #9ca3af;
                }
                .reply-content-snippet {
                    font-size: 11px;
                    color: #6b7280;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
            `}</style>

            {localNotes.length > 0 ? (
                <div className="custom-comment-scroll flex-grow-1" ref={scrollContainerRef} style={{ overflowY: 'auto', overflowX: 'hidden', maxHeight: isModal ? 'none' : '320px', paddingRight: '4px', marginBottom: '10px', display: 'flex', flexDirection: 'column' }}>
                    {(() => {
                        const renderSingleNote = (note, index, isPinnedPreview = false) => {
                            const isMe = String(note.user_id) === String(currentUserId);
                            const isEditing = editNoteId === note.id;

                            return (
                                <div key={note.id || index} className={`note-card ${note.pin_top && isPinnedPreview ? 'is-pinned' : ''}`}>
                                     <div className="note-header">
                                         <div style={{ width: '24px', height: '24px', minWidth: '24px', flexShrink: 0, backgroundColor: '#0DDFAB', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                             {(() => {
                                                 const avatarImg = note.user_image || mentionUsers?.find(u => String(u.id) === String(note.user_id))?.image;
                                                 return avatarImg
                                                     ? <PreviewZoom src={userImagePath + avatarImg} style={{ width: '100%', height: '100%' }}><AuthenticatedImage src={userImagePath + avatarImg} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></PreviewZoom>
                                                     : <span style={{ fontSize: '9px', fontWeight: '700', color: '#000', lineHeight: 1 }}>{note.user_name?.split(" ").map(n => n[0]).join("").toUpperCase()}</span>;
                                             })()}
                                         </div>
                                         <div className="note-user-info">
                                             <span className="note-user-name">{note.user_name}</span>
                                             <span className="note-date">{moment(note.createdAt || note.created_at).format('DD, MMMM, YYYY | hh:mm A')}</span>
                                         </div>
                                         {!isEditing && (
                                         <div className="note-actions">
                                             {isPinnedPreview ? (
                                                 <FaThumbtack
                                                     className="note-action-icon"
                                                     style={{ color: '#0DDFAB' }}
                                                     onClick={() => handlePinNote(note)}
                                                 />
                                             ) : (
                                                 <>
                                                     <FaThumbtack
                                                         className="note-action-icon"
                                                         style={{ color: Number(note.pin_top) === 1 ? '#0DDFAB' : '' }}
                                                         onClick={() => handlePinNote(note)}
                                                     />
                                                     {String(note.user_id) === String(currentUserId) && (
                                                         <FaPencilAlt
                                                             className="note-action-icon"
                                                             onClick={() => handleEditNote(note)}
                                                         />
                                                     )}
                                                     <FaReply
                                                         className="note-action-icon"
                                                         onClick={() => handleReply(note)}
                                                     />
                                                     {String(note.user_id) === String(currentUserId) && (
                                                         <FaTrashAlt
                                                             className="note-action-icon"
                                                             onClick={() => setNoteToDelete(note.id)}
                                                         />
                                                     )}
                                                 </>
                                             )}
                                         </div>
                                         )}
                                     </div>

                                     <div className="note-content">
                                         {(() => {
                                             let replyData = note.reply_to;
                                             if (typeof replyData === 'string') {
                                                 try { replyData = JSON.parse(replyData); } catch(e) { replyData = null; }
                                             }
                                             if (!replyData) return null;
                                             
                                             return (
                                                 <div className="reply-quote mb-2">
                                                     <div className="quote-user">{replyData.user_name}</div>
                                                     <div className="quote-content">{replyData.content}</div>
                                                 </div>
                                             );
                                         })()}
                                         {isEditing ? (
                                             <div className="edit-mode">
                                                 <MentionsInput
                                                     className="mentions-input-field"
                                                     value={UpdatedNote}
                                                     onChange={e => setUpdatedNote(e.target.value)}
                                                     onPaste={e => handleNotePaste(e, UpdatedNote, setUpdatedNote)}
                                                     onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveEdit(note.id); } }}
                                                     style={{
                                                         control: { fontSize: '13px', boxShadow: 'none', width: '100%', minHeight: '60px', overflow: 'visible' },
                                                         input: { border: '1px solid #d1d5db', borderRadius: '8px', padding: '8px 10px', outline: 'none', boxShadow: 'none', width: '100%', minHeight: '60px', resize: 'none' },
                                                         highlighter: { border: '1px solid transparent', padding: '8px 10px', minHeight: '60px' },
                                                         suggestions: {
                                                             list: {
                                                                 backgroundColor: 'white',
                                                                 border: '1px solid #eaecf0',
                                                                 borderRadius: '8px',
                                                                 fontSize: '11px',
                                                                 boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
                                                                 maxHeight: '200px',
                                                                 overflowY: 'auto',
                                                                 zIndex: 100000
                                                             },
                                                             item: {
                                                                 padding: '8px 12px',
                                                                 borderBottom: '1px solid #f3f4f6',
                                                                 color: '#374151',
                                                                 '&focused': {
                                                                     backgroundColor: '#f0fdf9',
                                                                     color: '#0DDFAB',
                                                                     fontWeight: '600'
                                                                 },
                                                             },
                                                         },
                                                     }}
                                                 >
                                                     <Mention trigger="@" data={mentionUsers} markup="@[__display__](__id__)" appendSpaceOnAdd />
                                                 </MentionsInput>
                                                 <div className="d-flex justify-content-end gap-2 mt-2">
                                                     <Button size="small" onClick={() => setEditNoteId(null)}>Cancel</Button>
                                                     <Button size="small" type="primary" onClick={() => handleSaveEdit(note.id)} style={{ background: '#0DDFAB', border: 'none', color: '#000' }}>Save</Button>
                                                 </div>
                                             </div>
                                         ) : <span style={{ whiteSpace: 'pre-wrap' }}>{renderCommentWithMentions(note.notes || note.content)}</span>}
                                         
                                         {note.attachment_file && (() => {
                                             const isPreview = note.attachment_file.startsWith('__preview__');
                                             const displayName = isPreview ? note.attachment_file.replace('__preview__', '') : note.attachment_file;
                                             const isImg = /\.(jpg|jpeg|png|gif|webp|svg|avif)$/i.test(displayName);
                                             const filePath = isPreview ? null : notePath + note.attachment_file;
                                             if (isPreview) {
                                                 return (
                                                     <div className="mt-2">
                                                         <span className="file-attachment-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#1D1D1D', color: '#0DDFAB', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', opacity: 0.6 }}>
                                                             <i className="uil-download-alt" style={{ fontSize: '14px' }} />
                                                             <span>{displayName}</span>
                                                             <span style={{ fontSize: '10px', opacity: 0.8 }}>(uploading...)</span>
                                                         </span>
                                                     </div>
                                                 );
                                             }
                                             
                                             if (isImg) {
                                                 return (
                                                     <div className="mt-2">
                                                         <AuthenticatedImage
                                                             src={filePath}
                                                             style={{ maxWidth: '100%', borderRadius: '8px', cursor: 'pointer' }}
                                                             onClick={() => openSecureFile(filePath, displayName)}
                                                         />
                                                     </div>
                                                 );
                                             }
                                             
                                             return (
                                                 <div className="mt-2">
                                                     <span
                                                         onClick={() => openSecureFile(filePath, displayName)}
                                                         className="file-attachment-chip"
                                                         style={{
                                                             display: 'inline-flex',
                                                             alignItems: 'center',
                                                             gap: '8px',
                                                             background: '#1D1D1D',
                                                             color: '#0DDFAB',
                                                             padding: '6px 12px',
                                                             borderRadius: '6px',
                                                             fontSize: '12px',
                                                             fontWeight: '600',
                                                             cursor: 'pointer',
                                                             transition: 'opacity 0.2s'
                                                         }}
                                                         onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
                                                         onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
                                                     >
                                                         <i className="uil-download-alt" style={{ fontSize: '14px' }} />
                                                         <span>{note.attachment_file.split('-').slice(1).join('-') || note.attachment_file}</span>
                                                     </span>
                                                 </div>
                                             );
                                         })()}
                                     </div>

                                     {!isEditing && !isPinnedPreview && <>
                                         {note.reactions && note.reactions.length > 0 && (
                                             <div className="note-reactions">
                                                 {(() => {
                                                     const reactionGroups = {};
                                                     note.reactions.forEach(r => {
                                                         if (!reactionGroups[r.emoji]) reactionGroups[r.emoji] = 0;
                                                         reactionGroups[r.emoji]++;
                                                     });
                                                     return Object.entries(reactionGroups).map(([emoji, count]) => (
                                                         <div key={emoji} className="px-2 py-1 rounded-pill border d-flex align-items-center gap-1" style={{ fontSize: '11px', background: '#f8f9fa' }}>
                                                             <span>{emoji}</span>
                                                             <span>{count}</span>
                                                         </div>
                                                     ));
                                                 })()}
                                             </div>
                                         )}
                                         <div className="note-footer">
                                             <FaThumbsUp className="footer-icon" onClick={() => handleReaction(note.id, '👍')} />
                                             <Popover
                                                 content={
                                                     <div style={{ display: 'flex', gap: '10px', padding: '4px 0' }}>
                                                         {['👍', '❤️', '😂', '😮', '😢', '🔥'].map(emoji => (
                                                             <span
                                                                key={emoji}
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    fontSize: '20px',
                                                                    transition: 'transform 0.2s',
                                                                    display: 'inline-block'
                                                                }}
                                                                onMouseOver={e => e.currentTarget.style.transform = 'scale(1.3)'}
                                                                onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
                                                                onClick={() => handleReaction(note.id, emoji)}
                                                             >
                                                                {emoji}
                                                             </span>
                                                         ))}
                                                     </div>
                                                 }
                                                 trigger="click"
                                                 placement="top"
                                                 getPopupContainer={() => document.body}
                                                 overlayStyle={{ zIndex: 200000 }}
                                             >
                                                 <div className="footer-icon-wrap" style={{ display: 'inline-flex', cursor: 'pointer', padding: '4px' }}>
                                                    <span className="emoji-mask-icon footer-icon" style={{ maskImage: "url(/assets/images/emojie.svg)", WebkitMaskImage: "url(/assets/images/emojie.svg)", width: 12, height: 12 }} />
                                                 </div>
                                             </Popover>
                                         </div>
                                     </>}
                                </div>
                            );
                        };

                        return (
                            <>
                                {pinnedNote && (
                                    <div style={{ position: 'sticky', top: 0, zIndex: 10, background: '#fff', marginBottom: '12px' }}>
                                        {chronologicalNotes.filter(n => Number(n.pin_top) === 1).map((note, index) => renderSingleNote(note, index, true))}
                                    </div>
                                )}
                                <div className="d-flex flex-column" style={{ marginTop: 'auto' }}>
                                    {chronologicalNotes.map((note, index) => renderSingleNote(note, index, false))}
                                </div>
                            </>
                        );
                    })()}
                </div>
            ) : <div className="flex-grow-1 d-flex align-items-center justify-content-center text-muted">No comments yet</div>}

            <div className="comment-input-wrapper mt-2">
                <style>{`
                    .comment-input-fieldset {
                        border: 1px solid #d1d5db;
                        border-radius: 8px;
                        padding: 8px 10px 6px 10px;
                        position: relative;
                        background: #fff;
                        transition: border-color 0.2s;
                    }
                    .comment-input-fieldset:focus-within {
                        border-color: #1d1d1d;
                    }
                    .comment-input-legend {
                        position: absolute;
                        top: -9px;
                        left: 10px;
                        background: #fff;
                        padding: 0 6px;
                        font-size: 11px;
                        color: #1d1d1d;
                        font-weight: 600;
                        pointer-events: none;
                    }
                    .comment-input-toolbar {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-top: 4px;
                    }
                    .left-tools {
                        display: flex;
                        gap: 6px;
                    }
                    .tool-btn {
                        width: 26px;
                        height: 26px;
                        border-radius: 4px;
                        border: none;
                        background: #f3f4f6;
                        color: #4b5563;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        cursor: pointer;
                        font-size: 12px;
                        transition: background 0.2s;
                    }
                    .tool-btn:hover {
                        background: #e5e7eb;
                    }
                    .right-tools {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                    }
                    .enter-hint {
                        font-size: 12px;
                        color: #4b5563;
                        font-weight: 500;
                    }
                    .send-btn {
                        width: 30px;
                        height: 30px;
                        border-radius: 6px;
                        border: none;
                        background: #0DDFAB;
                        color: #000;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        cursor: pointer;
                        transition: opacity 0.2s;
                    }
                    .send-btn:disabled {
                        opacity: 0.5;
                        cursor: not-allowed;
                    }
                    .attachment-preview-pill {
                        background: #1d1d1d;
                        border-radius: 5px;
                        padding: 4px 10px;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        margin-bottom: 10px;
                        width: fit-content;
                        max-width: 100%;
                    }
                    .pill-icon {
                        color: #0DDFAB;
                        font-size: 14px;
                    }
                    .pill-text {
                        color: #0DDFAB;
                        font-size: 13px;
                        font-weight: 500;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }
                    .pill-close {
                        color: #0DDFAB;
                        font-size: 14px;
                        cursor: pointer;
                        margin-left: 10px;
                    }
                `}</style>

                <div className="comment-input-fieldset">
                    <span className="comment-input-legend">Comment</span>

                    {replyingTo && (
                        <div className="reply-preview-banner">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="reply-label">Replying to {replyingTo.user_name}</span>
                                <FaTimes className="close-reply" onClick={() => setReplyingTo(null)} />
                            </div>
                            <div className="reply-content-snippet">{replyingTo.content}</div>
                        </div>
                    )}
                    
                    {noteAttachments.length > 0 && (
                        <div className="d-flex flex-column gap-2 mb-2">
                            {noteAttachments.map((att, idx) => (
                                <div key={idx} className="attachment-preview-pill">
                                    <FaDownload className="pill-icon" />
                                    <span className="pill-text">{att.name} Attached</span>
                                    <FaTimes className="pill-close" onClick={() => setNoteAttachments(prev => prev.filter((_, i) => i !== idx))} />
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="mentions-input-container">
                        <MentionsInput
                            inputRef={inputRef}
                            className="mentions-input-field"
                            placeholder="Add a comment..."
                            value={newComment}
                            onChange={e => setNewComment(e.target.value)}
                            onPaste={e => handleNotePaste(e, newComment, setNewComment)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleAddNote();
                                }
                            }}
                            forceSuggestionsAboveCursor={true}
                            style={{
                                control: { fontSize: '14px', border: 'none', boxShadow: 'none', maxHeight: '100px', overflowY: 'auto' },
                                input: { padding: '4px 0', border: 'none', outline: 'none', boxShadow: 'none', minHeight: '20px', maxHeight: '100px', overflowY: 'auto', fontSize: '14px', lineHeight: '1.4', background: 'transparent' },
                                highlighter: { padding: '4px 0', border: 'none', fontSize: '14px', lineHeight: '1.4', color: 'transparent', maxHeight: '100px', overflow: 'hidden' },
                                suggestions: {
                                    list: {
                                        backgroundColor: 'white',
                                        border: '1px solid #eaecf0',
                                        borderRadius: '8px',
                                        fontSize: '12px',
                                        boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
                                        maxHeight: '200px',
                                        overflowY: 'auto',
                                        zIndex: 100000
                                    },
                                    item: {
                                        padding: '8px 12px',
                                        borderBottom: '1px solid #f3f4f6',
                                        color: '#374151',
                                        '&focused': {
                                            backgroundColor: '#f0fdf9',
                                            color: '#0DDFAB',
                                            fontWeight: '600'
                                        },
                                    },
                                },
                            }}
                        >
                            <Mention trigger="@" data={mentionUsers} markup="@[__display__](__id__)" className="mentions-pill" appendSpaceOnAdd />
                        </MentionsInput>
                    </div>

                    <div className="comment-input-toolbar">
                        <div className="left-tools">
                            <input type="file" multiple ref={fileInputRef} hidden accept=".png,.jpg,.jpeg,.pdf,.docx,.csv,.txt,.zip,.rar,.sql,.ppt,.xls,.xlsx,.svg,.webp,.avif,.doc" onChange={e => { if (e.target.files) setNoteAttachments(prev => [...prev, ...Array.from(e.target.files)]); e.target.value = ''; }} />
                            <button className="tool-btn" onClick={() => { if (fileInputRef.current) { fileInputRef.current.value = ''; fileInputRef.current.click(); } }}><FaPlus /></button>
                            <div style={{ position: 'relative' }}>
                                <Popover
                                    content={
                                        <div style={{ border: 'none' }} onClick={e => e.stopPropagation()}>
                                            <EmojiPicker
                                                onEmojiClick={(emojiData) => {
                                                    setNewComment(prev => prev + emojiData.emoji);
                                                    setShowEmojiPicker(false);
                                                }}
                                                theme={Theme.LIGHT}
                                                skinTonesDisabled
                                                height={400}
                                                width={320}
                                                searchDisabled={false}
                                                previewConfig={{ showPreview: false }}
                                            />
                                        </div>
                                    }
                                    trigger="click"
                                    open={showEmojiPicker}
                                    onOpenChange={setShowEmojiPicker}
                                    placement="topLeft"
                                    overlayClassName="emoji-picker-popover"
                                    overlayStyle={{ zIndex: 200000 }}
                                >
                                    <button 
                                        className="tool-btn" 
                                        style={{ color: showEmojiPicker ? "#0DDFAB" : "currentColor" }}
                                        onClick={() => {
                                            setShowEmojiPicker(!showEmojiPicker);
                                        }}
                                    >
                                        <span className="emoji-mask-icon" style={{ maskImage: "url(/assets/images/emojie.svg)", WebkitMaskImage: "url(/assets/images/emojie.svg)", width: 14, height: 14 }} />
                                    </button>
                                </Popover>
                            </div>
                        </div>
                        <div className="right-tools">
                            <span className="enter-hint">Press enter or</span>
                            <button 
                                className="send-btn" 
                                onClick={handleAddNote}
                                disabled={!newComment.trim() && noteAttachments.length === 0}
                            >
                                <FaPaperPlane />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {noteToDelete && ReactDOM.createPortal(
            <div
                style={{ position: 'fixed', inset: 0, zIndex: 1000100, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                onClick={e => { if (e.target === e.currentTarget) setNoteToDelete(null); }}
            >
                <div style={{ background: '#fff', borderRadius: '12px', width: 380, maxWidth: '95vw', padding: '20px 20px 16px', boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <h5 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Delete Comment</h5>
                        <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#888', lineHeight: 1 }} onClick={() => setNoteToDelete(null)}>×</button>
                    </div>
                    <p style={{ fontSize: '14px', color: '#1D1D1D', marginBottom: '6px' }}>Are you sure you want to delete this comment?</p>
                    <p style={{ fontSize: '13px', color: '#ff4d4f', marginBottom: '16px' }}>
                        <i className="uil uil-exclamation-triangle me-1" />
                        This action cannot be undone.
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                        <button type="button" className="btn btn-light waves-effect" onClick={() => setNoteToDelete(null)} style={{ borderRadius: '5px', fontSize: '13px' }}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-danger waves-effect waves-light"
                            style={{ borderRadius: '5px', fontSize: '13px' }}
                            onClick={() => { handleDeleteNote(noteToDelete); setNoteToDelete(null); }}
                        >
                            Delete
                        </button>
                    </div>
                </div>
            </div>,
            document.body
        )}
        </>
    );
};

export default LeadComments;
